import React, { useEffect, useState, useRef } from "react";
import { Modal, Button, Row, Col, Table } from "react-bootstrap";
import {
  FaPrint,
  FaDownload,
  FaTimes,
  FaShare,
  FaExternalLinkAlt,
  FaTag,
} from "react-icons/fa";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import StudentService from "../../../services/studentService";

const ReceiptView = ({ show, onHide, transaction }) => {
  const [receiptData, setReceiptData] = useState(null);
  const [loading, setLoading] = useState(false);
  const receiptRef = useRef();

  useEffect(() => {
    if (show && transaction) {
      loadReceipt();
    }
  }, [show, transaction]);

  const loadReceipt = async () => {
    try {
      setLoading(true);
      // Use admin receipt endpoint
      const response = await StudentService.getAdminReceipt(transaction.id);
      console.log("Receipt data loaded:", response.data);
      setReceiptData(response.data);
    } catch (error) {
      toast.error("Failed to load receipt");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Open receipt in new tab with full options
  const handleOpenInNewTab = async () => {
    if (!receiptRef.current || !receiptData) return;

    try {
      toast.info("Preparing receipt...");

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        allowTaint: true,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");

      const newTab = window.open("", "_blank");
      if (!newTab) {
        toast.error("Please allow pop-ups to open new tab");
        return;
      }

      const isDiscount = receiptData.amount < 0;
      const receiptTitle = isDiscount ? "Discount-Receipt" : "Payment-Receipt";

      newTab.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${receiptTitle}-${receiptData.receipt_number}</title>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1">
          <style>
            body {
              font-family: Arial, sans-serif;
              background: #f5f5f5;
              margin: 0;
              padding: 20px;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .container {
              max-width: 800px;
              width: 100%;
            }
            .receipt-image {
              width: 100%;
              border: 1px solid #ddd;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              margin-bottom: 20px;
            }
            .actions {
              display: flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
              margin-bottom: 20px;
              padding: 15px;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 5px rgba(0,0,0,0.1);
            }
            .btn {
              padding: 12px 24px;
              border: none;
              border-radius: 5px;
              cursor: pointer;
              font-size: 16px;
              font-weight: bold;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              transition: all 0.3s;
            }
            .btn:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 10px rgba(0,0,0,0.2);
            }
            .btn-download {
              background: #28a745;
              color: white;
            }
            .btn-print {
              background: #007bff;
              color: white;
            }
            .btn-share {
              background: #17a2b8;
              color: white;
            }
            .btn-copy {
              background: #6c757d;
              color: white;
            }
            @media print {
              .actions { display: none; }
              body { background: white; padding: 0; }
              .receipt-image { box-shadow: none; border: none; }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="actions">
              <button class="btn btn-download" onclick="downloadReceipt()">
                ⬇️ Download Image
              </button>
              <button class="btn btn-print" onclick="window.print()">
                🖨️ Print
              </button>
              <button class="btn btn-share" onclick="shareReceipt()">
                📤 Share
              </button>
              <button class="btn btn-copy" onclick="copyToClipboard()">
                📋 Copy Image
              </button>
            </div>
            <img id="receipt-img" class="receipt-image" src="${imgData}" alt="Receipt">
          </div>

          <script>
            function downloadReceipt() {
              const link = document.createElement('a');
              link.download = '${receiptTitle}-${receiptData.receipt_number}.png';
              link.href = document.getElementById('receipt-img').src;
              link.click();
            }

            async function shareReceipt() {
              const img = document.getElementById('receipt-img');
              const response = await fetch(img.src);
              const blob = await response.blob();
              const file = new File([blob], '${receiptTitle}-${receiptData.receipt_number}.png', { type: 'image/png' });
              
              if (navigator.share) {
                navigator.share({
                  title: '${isDiscount ? 'Discount Receipt' : 'Payment Receipt'}',
                  text: '${isDiscount ? 'Discount receipt from Sensible Academy' : 'Payment receipt from Sensible Academy'}',
                  files: [file]
                }).catch(() => {
                  alert('Share cancelled or failed');
                });
              } else {
                copyImageToClipboard();
              }
            }

            async function copyImageToClipboard() {
              const img = document.getElementById('receipt-img');
              const response = await fetch(img.src);
              const blob = await response.blob();
              
              try {
                await navigator.clipboard.write([
                  new ClipboardItem({
                    [blob.type]: blob
                  })
                ]);
                alert('Image copied to clipboard!');
              } catch (err) {
                const textarea = document.createElement('textarea');
                textarea.value = img.src;
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
                alert('Image link copied to clipboard!');
              }
            }
          </script>
        </body>
        </html>
      `);

      newTab.document.close();
      toast.success("Receipt opened in new tab");
    } catch (error) {
      console.error("Error opening receipt in new tab:", error);
      toast.error("Failed to open receipt");
    }
  };

  const handleDownloadPDF = async () => {
    if (!receiptRef.current) return;

    try {
      toast.info("Generating PDF...");

      const canvas = await html2canvas(receiptRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
        logging: false,
        allowTaint: true,
        useCORS: true,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "px",
        format: [canvas.width * 0.75, canvas.height * 0.75],
      });

      pdf.addImage(
        imgData,
        "PNG",
        0,
        0,
        canvas.width * 0.75,
        canvas.height * 0.75,
      );
      const isDiscount = receiptData?.amount < 0;
      pdf.save(`${isDiscount ? 'Discount' : 'Receipt'}-${receiptData?.receipt_number || "receipt"}.pdf`);

      toast.success("Receipt downloaded successfully");
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error("Failed to generate PDF");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
    }).format(Math.abs(amount) || 0);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const numberToWords = (num) => {
    if (num === 0) return "Zero";

    const ones = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];

    const convertLessThanThousand = (n) => {
      if (n === 0) return "";
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100)
        return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
      return (
        ones[Math.floor(n / 100)] +
        " Hundred" +
        (n % 100 ? " " + convertLessThanThousand(n % 100) : "")
      );
    };

    let words = "";
    if (num >= 100000) {
      words += convertLessThanThousand(Math.floor(num / 100000)) + " Lakh ";
      num %= 100000;
    }
    if (num >= 1000) {
      words += convertLessThanThousand(Math.floor(num / 1000)) + " Thousand ";
      num %= 1000;
    }
    if (num > 0) {
      words += convertLessThanThousand(num);
    }
    return words.trim() + " Only";
  };

  const isDiscount = receiptData?.amount < 0;
  const displayAmount = receiptData?.amount || 0;

  if (!receiptData && !loading) return null;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isDiscount ? (
            <FaTag className="me-2 text-danger" />
          ) : (
            <FaPrint className="me-2" />
          )}
          {isDiscount ? "Discount Receipt" : "Payment Receipt"} - Sensible Academy
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            {/* Hidden receipt for capture */}
            <div
              style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
            >
              <div
                ref={receiptRef}
                className="p-4"
                style={{
                  backgroundColor: "white",
                  fontFamily: "Arial, sans-serif",
                  width: "700px",
                }}
              >
                {/* Institute Header */}
                <div
                  className="text-center mb-4"
                  style={{
                    borderBottom: "2px solid #333",
                    paddingBottom: "15px",
                  }}
                >
                  <h2 className="mb-1" style={{ color: "#2c3e50" }}>
                    Sensible Academy
                  </h2>
                  <p
                    className="mb-1"
                    style={{ fontSize: "1.1em", fontWeight: "bold" }}
                  >
                    Piyush Jain
                  </p>
                  <p className="mb-1">
                    B.E. (Comp. Sc.) | IBM Certified Trainer
                  </p>
                  <p className="mb-2" style={{ color: "#666" }}>
                    _________________________________
                  </p>
                  <p className="mb-1">
                    <strong>📞 Contacts:</strong> 77228-99444, 93013-51989
                  </p>
                  <p className="mb-1">
                    <strong>📧 Email:</strong> piyush@sensible-computers.com
                  </p>
                  <p className="mb-1">
                    <strong>🌐 Website:</strong> www.sensibleacademy.com
                  </p>
                  <p className="mb-1">
                    <strong>📍 Address:</strong> B-492, Cross Road-25, Junwani
                    Main Road
                  </p>
                  <p className="mb-3">Smriti Nagar, Bhilai (C.G.)</p>
                  <h4
                    className="py-2"
                    style={{
                      backgroundColor: isDiscount ? "#dc3545" : "#f8f9fa",
                      color: isDiscount ? "white" : "black",
                      borderTop: "1px solid #dee2e6",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    {isDiscount ? "DISCOUNT RECEIPT" : "PAYMENT RECEIPT"}
                  </h4>
                </div>

                {/* Receipt Details */}
                <Row className="mb-4">
                  <Col xs={6}>
                    <p>
                      <strong>Receipt No:</strong> {receiptData?.receipt_number}
                    </p>
                    <p>
                      <strong>Transaction ID:</strong>{" "}
                      {receiptData?.transaction_id}
                    </p>
                  </Col>
                  <Col xs={6} className="text-end">
                    <p>
                      <strong>Date:</strong>{" "}
                      {formatDate(receiptData?.payment_date)}
                    </p>
                    <p>
                      <strong>Type:</strong>{" "}
                      <span
                        className={`text-uppercase ${isDiscount ? "text-danger fw-bold" : ""}`}
                      >
                        {isDiscount ? "DISCOUNT" : receiptData?.payment_method}
                      </span>
                    </p>
                  </Col>
                </Row>

                {/* Student Details */}
                <div
                  className="mb-4 p-3"
                  style={{ backgroundColor: "#f8f9fa", borderRadius: "5px" }}
                >
                  <h5>Student Information</h5>
                  <Row>
                    <Col xs={6}>
                      <p className="mb-1">
                        <strong>Name:</strong> {receiptData?.student_name}
                      </p>
                      <p className="mb-1">
                        <strong>Email:</strong> {receiptData?.email}
                      </p>
                    </Col>
                    <Col xs={6}>
                      <p className="mb-1">
                        <strong>Phone:</strong> {receiptData?.phone}
                      </p>
                    </Col>
                  </Row>
                </div>

                {/* Payment/Discount Details */}
                <Table bordered className="mb-4">
                  <thead style={{ backgroundColor: "#e9ecef" }}>
                    <tr>
                      <th>Description</th>
                      <th className="text-end">Amount (₹)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <strong>{receiptData?.course_name}</strong>
                        <br />
                        <small className="text-muted">
                          {isDiscount 
                            ? `Discount Applied: ${receiptData?.remarks || "No reason provided"}`
                            : receiptData?.remarks || "Course fee payment"}
                        </small>
                      </td>
                      <td
                        className={`text-end fw-bold ${isDiscount ? "text-danger" : "text-success"}`}
                        style={{ fontSize: "1.1em" }}
                      >
                        {isDiscount 
                          ? `- ${formatCurrency(displayAmount)}`
                          : formatCurrency(displayAmount)}
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: "#f8f9fa" }}>
                      <td className="text-end">
                        <strong>Total Amount:</strong>
                      </td>
                      <td
                        className={`text-end fw-bold ${isDiscount ? "text-danger" : "text-success"}`}
                        style={{ fontSize: "1.2em" }}
                      >
                        {isDiscount 
                          ? `- ${formatCurrency(displayAmount)}`
                          : formatCurrency(displayAmount)}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* Amount in Words - Only for positive amounts or absolute value */}
                <div
                  className="mb-4 p-2"
                  style={{
                    borderTop: "1px dashed #dee2e6",
                    borderBottom: "1px dashed #dee2e6",
                  }}
                >
                  <p className="mb-0">
                    <strong>Amount in Words:</strong>{" "}
                    {receiptData?.amount_in_words || numberToWords(Math.abs(displayAmount))}
                  </p>
                </div>

                {/* Discount Reason Section (only for discount receipts) */}
                {isDiscount && receiptData?.remarks && (
                  <div
                    className="mb-4 p-3"
                    style={{ backgroundColor: "#fff3cd", borderRadius: "5px" }}
                  >
                    <p className="mb-0">
                      <strong>📝 Discount Reason:</strong> {receiptData?.remarks}
                    </p>
                  </div>
                )}

                {/* Footer */}
                <Row className="mt-5">
                  <Col xs={6}>
                    <p>Received by: ___________________</p>
                    <p className="mt-4">(Authorized Signatory)</p>
                  </Col>
                  <Col xs={6} className="text-end">
                    <p>For Sensible Academy</p>
                    <p className="mt-4">(Institute Stamp)</p>
                  </Col>
                </Row>

                <p
                  className="text-center text-muted mt-4 small"
                  style={{ borderTop: "1px solid #dee2e6", paddingTop: "10px" }}
                >
                  This is a computer generated receipt - valid without signature
                </p>
              </div>
            </div>

            {/* Preview of receipt */}
            <div className="text-center p-4 bg-light rounded">
              <p>
                {isDiscount ? (
                  <>
                    <FaTag className="text-danger me-2" size={24} />
                    <strong className="text-danger">Discount Receipt</strong>
                    <br />
                    Discount of {formatCurrency(displayAmount)} applied to {receiptData?.course_name}
                    <br />
                    <small className="text-muted">Reason: {receiptData?.remarks}</small>
                  </>
                ) : (
                  <>
                    <strong>Payment Receipt</strong>
                    <br />
                    Amount: {formatCurrency(displayAmount)}
                  </>
                )}
              </p>
              <p className="text-muted small mt-2">
                Click "Open in New Tab" to view full receipt
              </p>
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <FaTimes className="me-2" /> Close
        </Button>

        <Button
          variant="primary"
          onClick={handleOpenInNewTab}
          disabled={loading || !receiptData}
          title="Open receipt in new tab"
        >
          <FaExternalLinkAlt className="me-2" /> Open in New Tab
        </Button>

        <Button
          variant="success"
          onClick={handleDownloadPDF}
          disabled={loading || !receiptData}
          title="Download as PDF"
        >
          <FaDownload className="me-2" /> Download PDF
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ReceiptView;